import { GlobalSearchResultType } from '@kcftech/prometheus-client';
import { useQuery } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import { useStoredState } from 'custom_hooks/useStorage';
import { useCallback, useEffect, useState } from 'react';
import { excludeNullAndUndefined, take } from 'utils/array';
import {
    GlobalSearchTargetInfo,
    getPathForHierarchy,
    globalSearchTargetConfig,
} from 'utils/global_search';

const maxHistoricalTargets = 15;

const targetUpdaters: Readonly<{
    [key in GlobalSearchResultType]: (
        target: GlobalSearchTargetInfo,
    ) => Promise<GlobalSearchTargetInfo | undefined>;
}> = {
    Corporation: async (target: GlobalSearchTargetInfo) => {
        const { Name, IconUrl } = await sdClient.getCorporation({ id: target.entityId });
        return { ...target, entityName: Name, iconUrl: IconUrl };
    },
    Location: async (target: GlobalSearchTargetInfo) => {
        const { Name, Corporation } = await sdClient.getLocation({
            locationId: target.entityId,
        });

        return {
            ...target,
            entityName: Name,
            entityPath: [Corporation.CorporationName],
        };
    },
    System: async (target: GlobalSearchTargetInfo) => {
        const {
            Name,
            Corporation: { CorporationName },
            Location: { LocationName },
        } = await sdClient.getTreeItem({ treeItemId: target.entityId });

        return {
            ...target,
            entityName: Name,
            entityPath: [CorporationName, LocationName],
        };
    },
    Asset: async (target: GlobalSearchTargetInfo) => {
        const {
            Name,
            Corporation: { CorporationName },
            Location: { LocationName },
            Hierarchy,
        } = await sdClient.getTreeItem({ treeItemId: target.entityId });

        return {
            ...target,
            entityName: Name,
            entityPath: [
                CorporationName,
                LocationName,
                ...getPathForHierarchy(Hierarchy),
            ],
        };
    },
    BaseStation: (target) => Promise.resolve(target),
    Node: (target) => Promise.resolve(target),
};

/**
 * Refreshes the data associated with a target. Returns `undefined` if
 * the target no longer exists or there is an error.
 */
export async function refreshTarget(
    target: GlobalSearchTargetInfo,
): Promise<GlobalSearchTargetInfo | undefined> {
    if (!(target.entityType in targetUpdaters)) {
        return undefined;
    }
    const updater = targetUpdaters[target.entityType];

    try {
        return await updater(target);
    } catch {
        return undefined;
    }
}

export const globalSearchTargetExists = (name: string) =>
    globalSearchTargetConfig.some(({ targetName }) => targetName === name);

export const globalSearchHistoryKeyName = 'global-search-history';

/**
 * Hook to manage global search history stored in local storage.
 */
export function useGlobalSearchHistory() {
    const [initialized, setInitialized] = useState(false);
    const [searchHistory, setSearchHistory] = useStoredState<GlobalSearchTargetInfo[]>(
        globalSearchHistoryKeyName,
        [],
    );

    const { data: refreshedSearchHistory, isLoading } = useQuery({
        queryKey: ['global-search-history-refresh', searchHistory],

        queryFn: () =>
            Promise.all(
                searchHistory
                    .filter(({ targetName }) => globalSearchTargetExists(targetName))
                    .map((target) => refreshTarget(target)),
            ),

        enabled: !initialized,
    });

    useEffect(() => {
        if (refreshedSearchHistory && !initialized) {
            setSearchHistory(excludeNullAndUndefined(refreshedSearchHistory));
            setInitialized(true);
        }
    }, [initialized, refreshedSearchHistory, setSearchHistory]);

    const addToSearchHistory = useCallback(
        (target: GlobalSearchTargetInfo) => {
            setSearchHistory((history) =>
                take(
                    [target, ...history.filter((hist) => hist.url !== target.url)],
                    maxHistoricalTargets,
                ),
            );
        },
        [setSearchHistory],
    );

    const removeFromSearchHistory = useCallback(
        (target: GlobalSearchTargetInfo) => {
            setSearchHistory((history) =>
                history.filter((hist) => hist.url !== target.url),
            );
        },
        [setSearchHistory],
    );

    return {
        searchHistory: isLoading ? undefined : searchHistory,
        isLoading,
        addToSearchHistory,
        removeFromSearchHistory,
    };
}
